import { Card, CardContent, Typography, Stack, Divider, CardActionArea } from '@mui/material'
import Link from 'next/link'
import React from 'react'
import { useTranslation } from 'react-i18next'
import { ProfileBox } from '@omnea-app/fe-users-and-groups/ProfileBox'
import { PaymentIntervalEnum } from '@omnea-app/packages/universal/hasura/graphql'
import { getStageValueTranslationString } from '@omnea-app/packages/universal/requests/getStageValueTranslationString'
import { isDefined } from '@omnea-app/packages/universal/type-guards/isDefined'
import { useFormatRequestValue } from '../../detail/components/header/RequestHeader'
import { RequestPriorityController } from '../../priority/RequestPriorityController'
import { generateRequestDetailRoute, RequestTabs } from '../../requestRoutes'
import { RequestStage } from '../../utils/RequestStage'
import { RequestRow } from '../../utils/useRequestTableRows'

interface RequestMobileCardProps {
  request: RequestRow
}

function mapStringToPaymentInterval(paymentInterval: string | undefined): PaymentIntervalEnum | undefined {
  return PaymentIntervalEnum[paymentInterval as keyof typeof PaymentIntervalEnum]
}

export const RequestMobileCard: React.FC<RequestMobileCardProps> = ({ request }) => {
  const { t: tShared } = useTranslation('shared')

  const paymentInterval = mapStringToPaymentInterval(request.value?.paymentInterval)
  const requestValueWithPaymentInterval = request.value
    ? {
        ...request.value,
        paymentInterval,
      }
    : undefined

  const subtitleDetails = [
    { text: request.supplier && 'name' in request.supplier ? request.supplier.name : null },
    { text: useFormatRequestValue(requestValueWithPaymentInterval) },
    { text: request.publicId },
  ].filter((detail) => isDefined(detail.text))

  const stageValue = getStageValueTranslationString({ row: request, t: tShared }).firstItem
  const requestDetailHref = generateRequestDetailRoute(RequestTabs.OVERVIEW, request.id)
  return (
    <Card>
      <CardActionArea LinkComponent={Link} href={requestDetailHref} data-testid="request-mobile-card">
        <CardContent>
          <Stack gap={3}>
            <Stack gap={1}>
              <Typography variant="body-medium">{request.name}</Typography>
              {subtitleDetails.length ? (
                <Typography
                  noWrap
                  variant="body-regular"
                  color="text.muted"
                  sx={{
                    width: '100%',
                  }}
                >
                  {subtitleDetails.map((detail) => detail.text).join(' ∙ ')}
                </Typography>
              ) : null}
            </Stack>

            <Stack direction="row" justifyContent="space-between">
              <ProfileBox text={request.createdBy} avatarVariant="circular" userId={request.createdBy} disableTooltip />
              {request.priority && (
                <RequestPriorityController requestId={request.id} priority={request.priority} displayBorderOnHover readonly />
              )}
            </Stack>
          </Stack>
        </CardContent>
        {request.stage && request.stage.length > 0 && (
          <>
            <Divider />
            <CardContent>
              <Stack>
                <RequestStage row={request} stageValue={stageValue} />
              </Stack>
            </CardContent>
          </>
        )}
      </CardActionArea>
    </Card>
  )
}
